<template>
  <div class="Warning-Flipper" :id="domId" :class="[flipType, { go: isFlipping }]" :style="style">
    <div class="digital front" :style="warningFrontStyle"></div>
    <div class="digital back" :style="warningBackStyle"></div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, computed, watch } from "vue"
import { v4 } from "uuid"
const domId = ref(`uuid_${v4()}`)

interface DataTextProps {
  flipType?: string
  frontText?: {
    text: string
    color: string
    background: string
  }
  backText?: {
    text: string
    color: string
    background: string
  }
  style?: { [key: string]: string | number }
  time?: number
}
defineOptions({
  name: "TextFliper",
})
const props = withDefaults(defineProps<DataTextProps>(), {
  flipType: () => "down",
  frontText: () => ({ text: "", color: "#000", background: "#fff" }),
  backText: undefined,
  style: () => ({
    width: "120px",
    height: "36px",
    fontSize: "14px",
  }),
  time: () => 5000,
})

const style = computed(() => {
  const style = Object.assign(
    {
      width: "120px",
      height: "36px",
      fontSize: "14px",
    },
    props.style,
  )
  let { lineHeight, fontSize, height } = style
  if (!lineHeight) {
    lineHeight = height
  }
  return {
    ...props.style,
    lineHeight,
    fontSize,
    height,
  }
})

const isFlipping = ref(false)
const frontText = ref(props.frontText)
const backText = ref(props.backText)

watch(
  () => props.frontText,
  val => {
    isFlipping.value = true
    setTimeout(() => {
      isFlipping.value = false
      frontText.value = val
    }, props.time / 4)
  },
)
watch(
  () => props.backText,
  val => {
    setTimeout(() => {
      backText.value = val
    }, props.time / 4)
  },
)

const warningFrontStyle = computed(() => {
  if (!backText.value) {
    return {}
  }
  return {
    "--content-value": `"${frontText.value.text}"`,
    "--content-color": frontText.value.background,
    "--content-font": frontText.value.color,
    "--content-size": style.value.fontSize,
  }
})
const warningBackStyle = computed(() => {
  if (!backText.value) {
    return {}
  }
  return {
    "--content-value": `"${backText.value.text}"`,
    "--content-color": backText.value.background,
    "--content-font": backText.value.color,
    "--content-size": style.value.fontSize,
  }
})

const styleElement = ref<HTMLStyleElement | null>(null)

const updatePseudoElementStyle = () => {
  if (styleElement.value) {
    const sec = props.time / 4 / 1000
    let styleString = `
    #${domId.value}.down.go .front:before {
        animation: textFliperFrontFlipDown ${sec}s ease-in-out both;
    }
    #${domId.value}.down.go .back:after {
        animation: textFliperBackFlipDown ${sec}s ease-in-out both;
    }
    #${domId.value}.up.go .front:after {
        animation: textFliperFrontFlipUp ${sec}s ease-in-out both;
    }
    #${domId.value}.up.go .back:before {
        animation: textFliperBackFlipUp ${sec}s ease-in-out both;
    }
    `
    styleElement.value.innerHTML = styleString
  }
}

onMounted(async () => {
  styleElement.value = document.createElement("style")
  document.head.appendChild(styleElement.value)
  updatePseudoElementStyle()
})
</script>

<style lang="scss">
@keyframes textFliperFrontFlipDown {
  0% {
    transform: perspective(160px) rotateX(0deg);
  }

  100% {
    transform: perspective(160px) rotateX(-180deg);
  }
}

@keyframes textFliperBackFlipDown {
  0% {
    transform: perspective(160px) rotateX(180deg);
  }

  100% {
    transform: perspective(160px) rotateX(0deg);
  }
}

@keyframes textFliperFrontFlipUp {
  0% {
    transform: perspective(160px) rotateX(0deg);
  }

  100% {
    transform: perspective(160px) rotateX(180deg);
  }
}

@keyframes textFliperBackFlipUp {
  0% {
    transform: perspective(160px) rotateX(-180deg);
  }

  100% {
    transform: perspective(160px) rotateX(0deg);
  }
}
</style>
<style lang="scss" scoped>
.Warning-Flipper {
  display: inline-block;
  position: relative;
  border-radius: 5px;
  background: #fff;
  color: #fff;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.3);
  text-align: center;
  font-family: "Helvetica Neue";
}

.Warning-Flipper .digital:before,
.Warning-Flipper .digital:after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.Warning-Flipper .digital:before {
  top: 0;
  bottom: 50%;
  border-radius: 5px 5px 0 0;
}

.Warning-Flipper .digital:after {
  top: 50%;
  bottom: 0;
  border-radius: 0 0 5px 5px;
  line-height: 0;
}

.Warning-Flipper .front,
.Warning-Flipper .back {
  font-size: var(--content-size, 16px);
}

.Warning-Flipper .front:before,
.Warning-Flipper .front:after {
  content: var(--content-value);
  background: var(--content-color);
  color: var(--content-font);
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding: 0 0 0 5px;
  box-sizing: border-box;
}

.Warning-Flipper .back:before,
.Warning-Flipper .back:after {
  content: var(--content-value);
  background: var(--content-color);
  color: var(--content-font);
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding: 0 0 0 5px;
  box-sizing: border-box;
}

/*向下翻*/
.Warning-Flipper.down .front:before {
  z-index: 3;
}

.Warning-Flipper.down .back:after {
  z-index: 2;
  transform-origin: 50% 0%;
  transform: perspective(160px) rotateX(180deg);
}

.Warning-Flipper.down .front:after,
.Warning-Flipper.down .back:before {
  z-index: 1;
}

.Warning-Flipper.down.go .front:before {
  transform-origin: 50% 100%;
  /* animation: warningFrontFlipDown var(--pft-data-flip-animation-duration, 0.6s) ease-in-out both; */
  // box-shadow: 0 -2px 6px rgba(255, 255, 255, 0.3);
  backface-visibility: hidden;
}

/* .Warning-Flipper.down.go .back:after { */
/* animation: warningBackFlipDown var(--pft-data-flip-animation-duration, 0.6s) ease-in-out both; */
/* } */

/*向上翻*/
.Warning-Flipper.up .front:after {
  z-index: 3;
}

.Warning-Flipper.up .back:before {
  z-index: 2;
  transform-origin: 50% 100%;
  transform: perspective(160px) rotateX(-180deg);
}

.Warning-Flipper.up .front:before,
.Warning-Flipper.up .back:after {
  z-index: 1;
}

.Warning-Flipper.up.go .front:after {
  transform-origin: 50% 0;
  /* animation: warningFrontFlipUp 0.6s ease-in-out both; */
  // box-shadow: 0 2px 6px rgba(255, 255, 255, 0.3);
  backface-visibility: hidden;
}
</style>
